import React, { useMemo } from "react";
import moment from "moment";
import Barcode from "react-barcode";

import { ECard } from "libs/ui";
import { IAirportDetails } from "types";
import { getAirLineData, getJourneyType } from "utils/utility";
import FareSummary from "libs/ui/src/FareSummary/FareSummary";
import { IFlightOnlineBookingList } from "libs/data-access/types";

import { manageFlightCharges } from "../../hooks/utility";
import {
  getDuration,
  getDuration1G,
  getTimeDifference,
  normalizeAkbarJourney,
} from "../../hooks/utility/akbarAdapter";

interface ITicketJourney {
  type: string;
  airline: string;
  supplierPnrNumber: string;
  airlinesPnrNumber: string;
  travelClass: string;
  checkInBag: string;
  cabinBag: string;
  // segments: {
  //     fromCode: string;
  //     toCode: string;
  //     fromAirport: string;
  //     toAirport: string;
  //     fromTerminal: string;
  //     toTerminal: string;
  //     flightNumber: string;
  //     aircraftType: string;
  //     departureDateTime: string;
  //     arrivalDateTime: string;
  //     checkInBaggage: string;
  //     passengers: {
  //         paxId: number;
  //         title: string | null;
  //         firstName: string | null;
  //         lastName: string | null;
  //         dateOfBirth: string | null;
  //         gender: string | null;
  //         passportNumber: string | null;
  //         passportIssueDate: string | null;
  //         passportExpiryDate: string | null;
  //         selectedSsr: {
  //             code: string;
  //             description: string;
  //         }[];
  //     }[];
  // }[];

  segments: any[];
}

interface IProps {
  flightTicket: IFlightOnlineBookingList;
  airportDetails: IAirportDetails[];
  isInternational: boolean;
  withFare?: boolean;
}

export default function ETicketModern({
  flightTicket,
  airportDetails,
  isInternational,
  withFare = false,
}: IProps) {
  const booking = flightTicket?.bookingResponses?.[0];
  console.log("flighTicket Response:", flightTicket);

  const user = {
    email: flightTicket?.email ?? "Not Provided",
    mobile: flightTicket?.contactNumber ?? "Not Provided",
  };

  // console.log("Contact person:",user);

  const pnr = booking?.pnrDetail?.supplierPnr;
  const tickets = booking?.pnrDetail?.ticketNumber?.split("/");
  const bookingDate = booking?.bookingDate;
  const paymentStatus = flightTicket?.paymentOrder?.paymentStatus || "-";
  const status =
    booking?.galileoBookingDetailResponses?.[0]?.bookingStatus ||
    booking?.navitaireBookingDetailResponses?.[0]?.bookingStatus ||
    booking?.akbarBookingDetailResponses?.[0]?.bookingStatus ||
    "-";

  // Gather passengers
  let passengerList: {
    paxId?: number;
    title: string;
    firstName: string;
    lastName: string;
    gender: string;
    dateOfBirth: string;
    infantDetail?: {
      title: string;
      firstName: string;
      lastName: string;
      gender: string;
      dateOfBirth: string;
      passportNumber: string | null;
      passportIssueDate: string | null;
      passportExpiryDate: string | null;
    } | null;
  }[][] = [];

  if (booking?.galileoPassengerDetailResponses)
    passengerList.push(booking?.galileoPassengerDetailResponses);
  if (booking?.navitairePassengerDetailResponses)
    passengerList.push(booking?.navitairePassengerDetailResponses);
  if (booking?.akbarPassengerDetailResponses)
    passengerList.push(booking?.akbarPassengerDetailResponses);
  const passengers = passengerList.flat();

  // group journeys: galileo, navitaire and akbar from all booking responses
  const journeys: Array<ITicketJourney> = useMemo(() => {
    let journeyList: Array<ITicketJourney> = [];

    // process all booking responses
    (flightTicket?.bookingResponses || []).forEach((bookingResponse) => {
      // galileo journeys from this booking response
      (bookingResponse?.galileoBookingDetailResponses || []).forEach((j) => {
        const segments = (j.flightBookingSegments || []).map((segment) => ({
          ...segment,
          fromCode: segment.origin,
          toCode: segment.destination,
          fromAirport: airportDetails.find(
            (a) => a.airportCode === segment.origin,
          )?.airportName,
          toAirport: airportDetails.find(
            (a) => a.airportCode === segment.destination,
          )?.airportName,
          fromTerminal: `${segment?.originTerminal ? `(T${segment?.originTerminal})` : ""}`,
          toTerminal: `${segment?.destinationTerminal ? `(T${segment?.destinationTerminal})` : ""}`,
          flightNumber: `${segment.carrier} ${segment.flightNumber}`,
          aircraftType: segment.equipment,
          departureDateTime: segment.departureTime,
          arrivalDateTime: segment.arrivalTime,
          checkInBaggage: segment.maxWeight
            ? `${segment.maxWeight} ${segment.unit === "Kilograms" ? "kg" : "lbs"}`
            : "N/A",
          passengers:
            bookingResponse.galileoPassengerDetailResponses?.map(
              (passenger, index) => ({
                paxId: index + 1,
                title: passenger.title,
                firstName: passenger.firstName,
                lastName: passenger.lastName,
                dateOfBirth: passenger.dateOfBirth,
                gender: passenger.gender,
                passportNumber: passenger.passportNumber,
                passportIssueDate: passenger.passportIssueDate,
                passportExpiryDate: passenger.passportExpiryDate,
                selectedSsr: [],
              }),
            ) || [],
        }));

        const firstSeg = segments[0] || {};

        journeyList.push({
          type: getJourneyType(journeyList.length),
          airline: firstSeg.carrier || "-",
          supplierPnrNumber: bookingResponse?.pnrDetail?.providerPnr || "-",
          airlinesPnrNumber: bookingResponse?.pnrDetail?.supplierPnr || "-",
          travelClass: firstSeg.cabin || "-",
          checkInBag: firstSeg.checkInBaggage || "-",
          cabinBag: firstSeg?.maxWeight
            ? `${firstSeg.maxWeight} ${firstSeg?.unit === "Kilograms" ? "Kg" : "Lbs"}`
            : firstSeg?.numberOfPieces
              ? `${firstSeg?.numberOfPieces} Pc`
              : "N/A",
          segments,
        });
      });

      // navitaire journeys from this booking response
      (bookingResponse?.navitaireBookingDetailResponses || []).forEach((j) => {
        const segments = (j.navitaireBookingDetailSegments || []).map(
          (segment) => ({
            ...segment,
            fromCode: segment.origin,
            toCode: segment.destination,
            fromAirport: airportDetails.find(
              (a) => a.airportCode === segment.origin,
            )?.airportName,
            toAirport: airportDetails.find(
              (a) => a.airportCode === segment.destination,
            )?.airportName,
            fromTerminal: `${segment?.originTerminal ? `(T${segment?.originTerminal})` : ""}`,
            toTerminal: `${segment?.destinationTerminal ? `(T${segment?.destinationTerminal})` : ""}`,
            flightNumber: `${segment.carrierCode} ${segment.flightNumber}`,
            aircraftType: segment.equipmentType,
            departureDateTime: segment.originDateTime,
            arrivalDateTime: segment.destinationDateTime,
            checkInBaggage: isInternational ? "30 Kg" : "15 Kg",
            passengers:
              bookingResponse.navitairePassengerDetailResponses?.map(
                (passenger, index) => ({
                  paxId: index + 1,
                  title: passenger.title,
                  firstName: passenger.firstName,
                  lastName: passenger.lastName,
                  dateOfBirth: passenger.dateOfBirth,
                  gender: passenger.gender,
                  passportNumber: passenger.passportNumber,
                  passportIssueDate: passenger.passportIssueDate,
                  passportExpiryDate: passenger.passportExpiryDate,
                  selectedSsr: [],
                }),
              ) || [],
          }),
        );

        const firstSeg = segments[0] || {};

        journeyList.push({
          type: getJourneyType(journeyList.length),
          airline: firstSeg.carrierCode || "-",
          supplierPnrNumber: bookingResponse?.pnrDetail?.providerPnr || "-",
          airlinesPnrNumber: bookingResponse?.pnrDetail?.supplierPnr || "-",
          travelClass: "-",
          checkInBag: isInternational ? "30 Kg" : "15 Kg",
          cabinBag: "-",
          segments,
        });
      });

      // akbar journey from this booking response
      (bookingResponse?.akbarBookingDetailResponses || []).forEach((j) => {
        // Process each journey within akbar booking
        (j.akbarBookingDetailJourneys || []).forEach((akbarJourney) => {
          // Normalize Akbar segments using the adapter
          const normalizedSegments = normalizeAkbarJourney(
            akbarJourney,
            isInternational,
          );

          const segments = normalizedSegments.map((segment) => ({
            ...segment,
            fromCode: segment.origin,
            toCode: segment.destination,
            fromAirport: airportDetails.find(
              (a) => a.airportCode === segment.origin,
            )?.airportName,
            toAirport: airportDetails.find(
              (a) => a.airportCode === segment.destination,
            )?.airportName,
            fromTerminal: `${segment?.originTerminal ? `(T${segment?.originTerminal})` : ""}`,
            toTerminal: `${segment?.destinationTerminal ? `(T${segment?.destinationTerminal})` : ""}`,
            flightNumber: `${segment.carrier} ${segment.flightNumber}`,
            aircraftType: segment.aircraft || "-",
            departureDateTime: segment.departureTime,
            arrivalDateTime: segment.arrivalTime,
            checkInBaggage: segment.baggage,
            passengers:
              bookingResponse.akbarPassengerDetailResponses?.map(
                (passenger) => ({
                  paxId: passenger.paxId,
                  title: passenger.title,
                  firstName: passenger.firstName,
                  lastName: passenger.lastName,
                  dateOfBirth: passenger.dateOfBirth,
                  gender: passenger.gender,
                  passportNumber: passenger.passportNumber,
                  passportIssueDate: passenger.passportIssueDate,
                  passportExpiryDate: passenger.passportExpiryDate,
                  selectedSsr:
                    akbarJourney.Segments?.find(
                      (segment) =>
                        segment.origin === segment.origin &&
                        segment.destination === segment.destination,
                    )
                      ?.SSR?.filter((ssr) => ssr.paxId === passenger.paxId)
                      ?.map((ssr) => ({
                        code: ssr.Code,
                        description: ssr.Description,
                      })) || [],
                }),
              ) || [],
          }));

          const firstSeg = segments[0] || {};

          journeyList.push({
            type: getJourneyType(journeyList.length),
            airline: firstSeg.carrier || "-",
            supplierPnrNumber: bookingResponse?.pnrDetail?.providerPnr || "-",
            airlinesPnrNumber: bookingResponse?.pnrDetail?.supplierPnr || "-",
            travelClass: akbarJourney.cabin || "-",
            checkInBag: akbarJourney.inclusions?.Baggage || "-",
            cabinBag: "-",
            segments,
          });
        });
      });
    });

    return journeyList;
  }, [flightTicket]);

  // Calculate layover between segments within the same journey
  function getLayoverTime(arrival: string, nextDeparture: string) {
    if (!arrival || !nextDeparture) return null;
    const diff = moment(nextDeparture).diff(moment(arrival), "minutes");
    if (diff <= 0) return null;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours ? hours + "h " : ""}${mins}m`;
  }

  console.log("Final Journey:", journeys);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* HEADER SECTION */}
      <div className="px-8 py-4 flex justify-between items-center border-b border-gray-200 bg-[rgba(250,236,180,0.7)]">
        <div className="flex items-center gap-4">
          <img
            src={require("../../../../../assets/logos/travelyatra.jpeg")}
            alt="Airline Logo"
            className="h-20 rounded"
          />
          <div className="flex flex-col">
            <div className="font-semibold text-gray-800 text-[14px]">
              PAUL MERCHANTS LTD
            </div>
            <div className="font-medium text-[10px] text-gray-800">
              SCO 829-30, SECTOR 22-A, CHANDIGARH
            </div>
            <div className="font-medium text-[12px] text-gray-800">
              0172-5041740
            </div>
            <div className="font-medium text-[12px] text-gray-800">
              issuance@paulmerchants.net
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 text-right gap-2">
          {/* <div>
                        <div className="text-xs text-gray-500">Status</div>
                        <div className="font-bold text-green-700 text-base">{humanizeText(status)?.toUpperCase() || "-"}</div>
                        </div> */}
          {/* <div></div> */}
          <div className="text-center">
            <div className="font-medium text-[12px] text-gray-900">
              Date of Booking*
            </div>
            <div className="font-semibold text-gray-800 text-base">
              {bookingDate
                ? moment.parseZone(bookingDate).format("DD MMM YYYY HH:mm")
                : "-"}
            </div>
          </div>

          <div className="text-center">
            <div className="font-medium text-[12px] text-gray-900">
              Payment Status
            </div>
            <div className="font-semibold text-green-700 text-base">
              {paymentStatus}
            </div>
          </div>
          <div className="text-center">
            <div className="font-medium text-[12px] text-gray-900">
              E-Ticket
            </div>
            <div className="font-semibold text-base">Paul Merchants Ltd</div>
          </div>
        </div>
      </div>
      <div className="px-8 py-2 text-xs text-gray-500  border-b border-gray-100">
        *Booking Date and all other timings are in UTC.
      </div>

      {/* JOURNEY GROUPS */}
      <div className="px-8 py-6">
        {journeys.map((journey, idx) => (
          <div
            key={idx}
            className="mb-8 border-[1.5px] border-gray-400 shadow-sm "
          >
            {/* JOURNEY HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between  px-6 py-4">
              <div className="flex flex-col gap-1">
                <div className="text-sm font-bold text-gray-800">
                  {journey.type}
                  <span className="font-normal">
                    {journey.segments.length > 0
                      ? ` ${journey.segments[0]?.fromCode?.split(" ")[0]} → ${journey.segments[journey.segments.length - 1]?.toCode?.split(" ")[0]}`
                      : ` ${journey.segments[0]?.origin || "N/A"} → ${journey.segments[0]?.destination || "N/A"}`}
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  Airline:{" "}
                  {/* <span className="font-semibold">{journey.airline}</span> */}
                  <span className="font-semibold">
                    {getAirLineData(journey.airline).name}
                  </span>
                </div>
              </div>
              <div className="flex flex-row gap-4 text-xs">
                {journey.supplierPnrNumber && (
                  <div
                    className="font-semibold text-gray-800 flex items-center px-2 py-1 rounded"
                    style={{
                      // paddingBottom: "10px",
                      // paddingLeft: "8px",
                      // paddingRight: "6px",
                      // borderRadius: "5px",
                    }}
                  >
                    GDS PNR:&nbsp;
                    <span className="font-normal tracking-widest">
                      {journey.supplierPnrNumber?.split("|")[0]}
                    </span>
                  </div>
                )}
                <div
                  className="font-semibold text-gray-800 px-2 py-1 rounded"
                  style={{
                    // paddingBottom: "10px",
                    // paddingLeft: "8px",
                    // paddingRight: "6px",
                    // borderRadius: "5px",
                  }}
                >
                  Airline PNR:&nbsp;
                  <span className="font-normal tracking-widest">
                    {journey.airlinesPnrNumber?.split("|")[0]}
                  </span>
                </div>

                {/* {tickets && tickets !== null && (
                  <div className="text-base flex items-center gap-1 font-semibold text-gray-800">
                    Tickets:{" "}
                    <span className="font-normal tracking-widest">
                      {tickets?.map((val, idx) => (
                        <p key={idx}>{val}</p>
                      ))}
                    </span>
                  </div>
                )} */}
              </div>
            </div>

            {/* SEGMENT TABLE */}
            {journey.segments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full  rounded text-xs">
                  <thead className="bg-[rgba(250,236,180,0.7)]">
                    <tr className=" text-gray-700">
                      <th className="border-l-0 border border-gray-700 px-2 py-2">
                        Flight Number
                      </th>
                      <th className="border border-gray-700 px-2 py-2">
                        From (Terminal)
                      </th>
                      <th className="border border-gray-700 px-2 py-2">
                        Departure Date & Time
                      </th>
                      <th className="border border-gray-700 px-2 py-2">
                        Travel Time
                      </th>
                      <th className="border border-gray-700 px-2 py-2">
                        To (Terminal)
                      </th>
                      <th className="border border-gray-700 px-2 py-2">
                        Arrival Date & Time
                      </th>
                      <th className="border-r-0 border border-gray-700 px-2 py-2">
                        Check in Bag
                      </th>
                      <th className="border-r-0 border border-gray-700 px-2 py-2">
                        Travel Class
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {journey.segments.map((segment, sidx) => (
                      <React.Fragment key={sidx}>
                        <tr className="text-center">
                          <td className="border-l-0 border border-gray-600 px-2 py-2 font-medium">
                            {/* {segment.flightNumber || "-"} */}
                            <div>
                              <div className="flex gap-1 items-center mb-1">
                                <div className="h-10 w-10 rounded-md">
                                  <img
                                    src={
                                      getAirLineData(
                                        segment.carrier || segment.carrierCode,
                                      ).logo
                                    }
                                    alt="flightTicket logo"
                                    loading="lazy"
                                    className="h-10 w-10 object-cover rounded-lg"
                                  />
                                </div>
                                <div>
                                  <p className="text-xs font-semibold min-w-[100px]">
                                    {
                                      getAirLineData(
                                        segment.carrier || segment.carrierCode,
                                      ).name
                                    }
                                  </p>
                                  <div className="flex items-center justify-center gap-3">
                                    <p className="text-xs">{`${segment.flightNumber}`}</p>
                                    <p className="text-xs">
                                      ({segment.aircraftType || "-"})
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.fromCode || "-"}{" "}
                            {segment.fromTerminal || ""}
                            <br />
                            {segment.fromAirport
                              ? segment.fromAirport
                                  .split(",")
                                  .map((item: string) => (
                                    <>
                                      <span>{item.trim()}</span>
                                      <br />
                                    </>
                                  ))
                              : ""}
                          </td>
                          <td className="border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.departureDateTime &&
                              moment
                                .parseZone(segment.departureDateTime)
                                .format("DD MMM YY, HH:mm")}
                          </td>
                          <td className="border border-gray-600 px-2 py-2 font-medium min-w-[70px] text-xs">
                            {booking?.akbarBookingDetailResponses?.length && (
                              <p>{segment.duration || "-"}</p>
                            )}
                            {booking?.navitaireBookingDetailResponses?.length &&
                              getDuration(
                                segment.originDateTime,
                                segment.arrvLTV,
                                segment.destinationDateTime,
                                segment.deptLTV,
                              )}
                            {booking?.galileoBookingDetailResponses?.length &&
                              getDuration1G(
                                segment.departureTime,
                                segment.arrivalTime,
                              )}
                          </td>
                          <td className="border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.toCode || "-"} {segment.toTerminal || ""}
                            <br />
                            {segment.toAirport
                              ? segment.toAirport
                                  .split(",")
                                  .map((item: string) => (
                                    <>
                                      <span>{item.trim()}</span>
                                      <br />
                                    </>
                                  ))
                              : ""}
                          </td>
                          <td className="border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.arrivalDateTime &&
                              moment
                                .parseZone(segment.arrivalDateTime)
                                .format("DD MMM YY, HH:mm")}
                          </td>
                          <td className="border-r-0 border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.checkInBaggage || "-"}
                          </td>
                          <td className="border-r-0 border border-gray-600 px-2 py-2 font-medium text-xs">
                            {segment.cabin === "E" ||
                            segment.cabin === "Economy"
                              ? "Economy"
                              : "Economy"}
                          </td>
                        </tr>

                        {/* LAYOVER BETWEEN SEGMENTS WITHIN SAME JOURNEY */}
                        {sidx < journey.segments.length - 1 && (
                          <tr>
                            <td
                              colSpan={8}
                              className="border-t border-b border-gray-600 bg-[linear-gradient(46deg,_#86d0c1,_#819bb95e)]"
                            >
                              <div
                                className="text-yellow-900 text-center  text-xs font-medium"
                                style={{
                                  marginBottom: "4px",
                                  marginTop: "4px",
                                }}
                              >
                                At {segment.toAirport} Layover Time :{" "}
                                {getLayoverTime(
                                  segment.arrivalDateTime,
                                  journey.segments[sidx + 1].departureDateTime,
                                ) || "-"}
                              </div>
                            </td>
                          </tr>
                        )}
                        {sidx === journey.segments.length - 1 && (
                          <tr>
                            <td colSpan={8}>
                              {/* SEGMENT WISE PAX-SSR MAPPING */}
                              <table className="w-full text-xs">
                                <thead className="bg-[rgba(250,236,180,0.7)]">
                                  <tr className=" text-gray-700">
                                    <th
                                      className="border-r-[1.5px] border-gray-300"
                                      style={{
                                        paddingBottom: "6px",
                                      }}
                                    >
                                      S.No.
                                    </th>
                                    <th
                                      className="border-r-[1.5px] border-gray-300"
                                      style={{
                                        paddingBottom: "6px",
                                      }}
                                    >
                                      Passenger
                                    </th>
                                    <th
                                      className="border-r-[1.5px] border-gray-300"
                                      style={{
                                        paddingBottom: "6px",
                                      }}
                                    >
                                      Ticket No.
                                    </th>
                                    <th
                                      className=""
                                      style={{
                                        paddingBottom: "6px",
                                      }}
                                    >
                                      Service Purchased
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {segment.passengers?.map(
                                    (eachPassenger: any, index: number) => (
                                      <tr
                                        key={`passenger-${index}`}
                                        className="text-center"
                                      >
                                        <td className="border-r-[1.5px] border-t-[1.5px] border-gray-300 px-2 py-2 font-medium text-xs">
                                          {index + 1}
                                        </td>
                                        <td className="border-r-[1.5px] border-t-[1.5px] border-gray-300 px-2 py-2 font-medium text-xs">
                                          <span>
                                            {eachPassenger?.title}{" "}
                                            {eachPassenger?.firstName}{" "}
                                            {eachPassenger?.lastName}
                                          </span>
                                        </td>
                                        <td className="border-r-[1.5px] border-t-[1.5px] border-gray-300 text-xs">
                                          {tickets?.[index] ?? tickets?.[0]}
                                        </td>
                                        <td className="border-t-[1.5px] border-gray-300 px-2 py-2 font-medium text-xs">
                                          {eachPassenger?.selectedSsr
                                            ?.map(
                                              (ssr: {
                                                code: string;
                                                description: string;
                                              }) =>
                                                `${ssr?.code} (${ssr?.description})`,
                                            )
                                            .join(", ")}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No flight segments available for this journey.
              </div>
            )}

            {/* PAX DETAILS */}
            {passengers.map((pax, paxIndex) => (
              <React.Fragment key={`${paxIndex}_pax`}>
                <div
                  className={`px-6 py-3 ${(paxIndex + 1) % 2 === 0 ? "bg-[#f0f8ff]" : "bg-[#dcdcdc]"}`}
                >
                  {/* PAX NAME */}
                  <div className="flex justify-between items-center">
                    <div className="text-xs font-semibold text-gray-800">
                      <div className="font-medium text-gray-700 text-xs">
                        Passenger - {paxIndex + 1}/{passengerList.flat().length}
                      </div>
                      {pax?.title || ""} {pax?.firstName || ""}{" "}
                      {pax?.lastName || ""}
                    </div>
                    {/* BARCODE */}
                    {status !== "FAILED" ? (
                      <Barcode
                        width={0.7}
                        height={30}
                        format="CODE128"
                        displayValue={false}
                        fontSize={8}
                        background="#ffffff"
                        lineColor="#000000"
                        value={`${pax?.title || ""}${pax?.firstName || ""}${pax?.lastName || ""} ${pnr || ""} ${journey?.segments[0]?.fromCode}${journey?.segments[journey?.segments.length - 1]?.toCode}${journey?.segments[0]?.flightNumber}`}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>

                {/* INFANT DETAILS */}
                {pax?.infantDetail && (
                  <div className="px-6 py-3 bg-white border-b border-gray-100">
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-semibold text-gray-800">
                        <div className="font-medium text-blue-600 text-xs">
                          <span className="text-xs text-blue-600 mr-1">
                            Infant
                          </span>
                          with Passenger {paxIndex + 1}
                        </div>
                        {pax.infantDetail?.title || ""}{" "}
                        {pax.infantDetail?.firstName || ""}{" "}
                        {pax.infantDetail?.lastName || ""}
                      </div>
                      {/* BARCODE FOR INFANT */}
                      {status !== "FAILED" ? (
                        <Barcode
                          width={0.7}
                          height={30}
                          format="CODE128"
                          displayValue={false}
                          fontSize={8}
                          background="#ffffff"
                          lineColor="#000000"
                          value={`${pax.infantDetail?.title || ""}${pax.infantDetail?.firstName || ""}${pax.infantDetail?.lastName || ""} ${pnr || ""} ${journey?.segments[0]?.fromCode}${journey?.segments[journey?.segments.length - 1]?.toCode}${journey?.segments[0]?.flightNumber}`}
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
            <div className="py-2 px-5">
              <p className="text-sm font-semibold">Traveler Information:</p>
              <div className="flex flex-col ps-1">
                <p className="text-xs">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
                <p className="text-xs">
                  <span className="font-medium">Mobile:</span> {user.mobile}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fare Summary */}
      {withFare && (
        <div className="px-8 pb-8">
          <ECard
            header={
              <div className="flex items-center text-lg font-semibold text-gray-800">
                <span className="mr-2">💰</span>Fare Summary
              </div>
            }
          >
            <FareSummary
              service="FLIGHT"
              refundCharges={manageFlightCharges(flightTicket)}
              airPriceJourneyResponseList={flightTicket?.paymentOrder}
            />
          </ECard>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 text-center space-y-2 py-6 border-t-[2px] border-gray-400">
        <p className="text-xs text-gray-500">
          This is a computer-generated e-ticket and does not require a
          signature.
        </p>
        <p className="text-xs text-gray-600">
          For any assistance, please contact our customer support at
          issuance@paulmerchants.net.
        </p>
        <p className="text-xs text-gray-600 font-semibold">
          Travel Yatra powered by Paul Merchants.
        </p>
      </div>
    </div>
  );
}
